import { v4 as uuid } from 'uuid';

import { DOCUMENT_UPLOAD_CONCURRENCY } from '~/common/constants/files';
import { UploadField } from '~/features/upload/types';
import { pilot } from '~/services/serviceInstances';
import { mapWithConcurrency } from '~/utils/mapWithConcurrency';

interface Request {
  files: File[];
  folderId: string;
  uploadFormFields: UploadField[];
}

interface Response {
  status: 'success' | 'failed';
  file: File;
}

type UploadedFiles = Response[];

export const addUploadFiles = async (
  request: Request,
): Promise<UploadedFiles> => {
  const { files, uploadFormFields, folderId } = request;

  return mapWithConcurrency(
    files,
    DOCUMENT_UPLOAD_CONCURRENCY,
    async (file) => {
      const formData = mapRequestToFormData(file, folderId, uploadFormFields);

      try {
        // The service-wide 2 minute timeout counts from when the request is
        // sent, not from when the browser grants it a connection, so a large
        // file (or one waiting behind the pool) aborts before it reaches the
        // server.
        await pilot.post('/document/upload/', formData, { timeout: 0 });
        return { status: 'success' as const, file };
      } catch {
        return { status: 'failed' as const, file };
      }
    },
  );
};

const mapRequestToFormData = (
  file: File,
  folderId: string,
  uploadFormFields: UploadField[],
) => {
  const transformedUploadFormFields = uploadFormFields.map(
    ({ fieldId, value }) => ({ id: String(fieldId), value }),
  );
  const formData = new FormData();

  formData.append('id', uuid());
  formData.append(
    'upload_form_data',
    JSON.stringify(transformedUploadFormFields),
  );
  formData.append('file', file);
  formData.append('filename', file.name);
  formData.append('folder', folderId);
  formData.append('size_in_bytes', String(file.size));

  return formData;
};
